import { VisualizationNode, VisualizationLink, getLinkEndpointId } from '../types/infrastructure';

export interface NodeRole {
  role: 'spine' | 'leaf' | 'server' | 'storage' | 'management' | 'ring_switch' | 'ring_host' | 'dragonfly_switch' | 'dragonfly_host' | 'unknown';
  layer: number; // Vertical layer: 0=spine (top), 1=leaf, 2=server (bottom) OR ring position OR group position
  confidence: number; // 0-1 confidence in role assignment
  group?: number; // Group ID for dragonfly topology
}

export interface AutoLayoutConfig {
  enableVerticalSeparation: boolean;
  layerSpacing: number; // Vertical spacing between layers
  nodeSpacing: number; // Horizontal spacing between nodes
  forceStrength: number; // Strength of layout forces
  layoutType?: 'auto' | 'hierarchical' | 'ring' | 'dragonfly'; // Manual layout type override
}

export class AutoLayoutEngine {
  private nodes: VisualizationNode[];
  private links: VisualizationLink[];
  private nodeRoles: Map<string, NodeRole>;
  private config: AutoLayoutConfig;

  constructor(nodes: VisualizationNode[], links: VisualizationLink[], config: AutoLayoutConfig) {
    this.nodes = nodes;
    this.links = links;
    this.nodeRoles = new Map();
    this.config = config;
  }

  // Analyze connectivity patterns to determine node roles
  analyzeNodeRoles(): Map<string, NodeRole> {
    const connectivity = this.buildConnectivityGraph();

    // First pass: identify device types based on name patterns and connectivity
    const deviceTypes = new Map<string, 'host' | 'switch'>();
    for (const node of this.nodes) {
      if (node.type === 'device') {
        const deviceType = node.metadata?.deviceType || '';
        const nodeName = node.name.toLowerCase();

        // Name-based detection
        if (deviceType === 'HOST' || deviceType.includes('server') || deviceType.includes('host') || nodeName.includes('server') || nodeName.includes('host')) {
          deviceTypes.set(node.id, 'host');
        } else if (deviceType.includes('SWITCH') || deviceType.includes('switch') || nodeName.includes('switch')) {
          deviceTypes.set(node.id, 'switch');
        } else {
          // Connectivity-based detection: analyze connection patterns
          const connections = connectivity.get(node.id) || new Map();
          const connectionCount = connections.size;

          // Check if this device has switch-like characteristics:
          // 1. High connectivity (connects to many other devices)
          // 2. Acts as aggregation point (other devices connect to it)
          if (connectionCount >= 3) {
            // Count how many connected devices have lower connectivity (leaf/host pattern)
            let lowerConnectivityCount = 0;
            connections.forEach((_, connectedNodeId) => {
              const connectedConnections = connectivity.get(connectedNodeId)?.size || 0;
              if (connectedConnections <= connectionCount / 2) {
                lowerConnectivityCount++;
              }
            });

            // If majority of connections go to lower-connectivity devices, this is likely a switch
            if (lowerConnectivityCount >= connectionCount * 0.6) {
              deviceTypes.set(node.id, 'switch');
            }
          }

          // Still unclassified? Check device name patterns for tier/layer indicators
          if (!deviceTypes.has(node.id)) {
            if (deviceType.includes('TIER') || deviceType.includes('tier') ||
                nodeName.includes('tier') || nodeName.includes('spine') ||
                nodeName.includes('leaf') || nodeName.includes('aggreg')) {
              deviceTypes.set(node.id, 'switch');
            } else {
              // Default fallback: assume compute/host type
              deviceTypes.set(node.id, 'host');
            }
          }
        }
      }
    }

    // Check topology type and assign roles accordingly
    const layoutType = this.config.layoutType || 'auto';

    /*console.log('AutoLayout topology detection:', {
      configLayoutType: this.config.layoutType,
      effectiveLayoutType: layoutType,
      switches: Array.from(deviceTypes.entries()).filter(([_, type]) => type === 'switch').length,
      hosts: Array.from(deviceTypes.entries()).filter(([_, type]) => type === 'host').length,
      deviceTypes: Array.from(deviceTypes.entries()).map(([id, type]) => {
        const node = this.nodes.find(n => n.id === id);
        return { id, name: node?.name, type, deviceType: node?.metadata?.deviceType };
      })
    });*/

    if (layoutType === 'hierarchical') {
      // Force hierarchical topology regardless of detection
      this.assignHierarchicalRoles(connectivity, deviceTypes);
    } else if (layoutType === 'ring') {
      // Force ring topology - use same logic as auto-detection but skip detection
      const ringDetection = this.detectRingTopology(connectivity, deviceTypes);
      if (ringDetection.isRing) {
        // Use actual ring if detected
        this.assignRingRoles(ringDetection, deviceTypes);
      } else {
        // Force artificial ring if no natural ring exists
        this.forceRingRoles(deviceTypes);
      }
    } else if (layoutType === 'dragonfly') {
      // Force dragonfly topology - use same logic as auto-detection but skip detection
      const dragonflyDetection = this.detectDragonflyTopology(connectivity, deviceTypes);
      if (dragonflyDetection.isDragonfly) {
        // Use actual dragonfly if detected
        this.assignDragonflyRoles(dragonflyDetection, deviceTypes);
      } else {
        // Force artificial dragonfly if no natural dragonfly exists
        this.forceDragonflyRoles(deviceTypes);
      }
    } else {
      // Auto-detect topology (original logic)
      const ringDetection = this.detectRingTopology(connectivity, deviceTypes);
      const dragonflyDetection = this.detectDragonflyTopology(connectivity, deviceTypes);

      /*console.log('Topology auto-detection results:', {
        ring: { isRing: ringDetection.isRing, confidence: ringDetection.confidence },
        dragonfly: { isDragonfly: dragonflyDetection.isDragonfly, confidence: dragonflyDetection.confidence, groups: dragonflyDetection.groups.size }
      });*/

      if (dragonflyDetection.isDragonfly) {
        this.assignDragonflyRoles(dragonflyDetection, deviceTypes);
      } else if (ringDetection.isRing) {
        this.assignRingRoles(ringDetection, deviceTypes);
      } else {
        this.assignHierarchicalRoles(connectivity, deviceTypes);
      }
    }

    /*console.log('Node role analysis:', Array.from(this.nodeRoles.entries()).map(([id, role]) => {
      const node = this.nodes.find(n => n.id === id);
      return {
        node: node?.name,
        deviceType: node?.metadata?.deviceType,
        role: role.role,
        layer: role.layer,
        confidence: role.confidence
      };
    }));*/

    return this.nodeRoles;
  }

  // Build connectivity graph with connection counts and patterns
  private buildConnectivityGraph(): Map<string, Map<string, number>> {
    const graph = new Map<string, Map<string, number>>();

    // Initialize connectivity map
    for (const node of this.nodes) {
      graph.set(node.id, new Map());
    }

    // Count connections between nodes
    for (const link of this.links) {
      const sourceId = getLinkEndpointId(link.source);
      const targetId = getLinkEndpointId(link.target);

      // Handle component-level connections by finding parent devices
      const sourceNodeId = this.getDeviceFromComponent(sourceId);
      const targetNodeId = this.getDeviceFromComponent(targetId);

      if (sourceNodeId && targetNodeId && sourceNodeId !== targetNodeId) {
        const sourceConnections = graph.get(sourceNodeId) || new Map();
        const targetConnections = graph.get(targetNodeId) || new Map();

        sourceConnections.set(targetNodeId, (sourceConnections.get(targetNodeId) || 0) + 1);
        targetConnections.set(sourceNodeId, (targetConnections.get(sourceNodeId) || 0) + 1);

        graph.set(sourceNodeId, sourceConnections);
        graph.set(targetNodeId, targetConnections);
      }
    }

    return graph;
  }

  // Extract device ID from component ID (e.g., "server-1/nic-0" -> "server-1")
  private getDeviceFromComponent(nodeId: string): string | null {
    if (nodeId.includes('/')) {
      return nodeId.split('/')[0];
    }

    // Check if this is a device node
    const node = this.nodes.find(n => n.id === nodeId && n.type === 'device');
    return node ? nodeId : null;
  }

  // Detect if the topology forms a ring pattern
  private detectRingTopology(connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): {
    isRing: boolean;
    ringOrder: string[];
    confidence: number;
  } {
    const switches = Array.from(deviceTypes.entries())
      .filter(([_, type]) => type === 'switch')
      .map(([id]) => id);

    if (switches.length < 3) {
      return { isRing: false, ringOrder: [], confidence: 0 };
    }

    // Check if switches form a ring (each switch connects to exactly 2 other switches)
    const switchConnections = new Map<string, string[]>();

    for (const switchId of switches) {
      const connections = connectivity.get(switchId) || new Map();
      const connectedSwitches: string[] = [];

      for (const peerId of Array.from(connections.keys())) {
        if (deviceTypes.get(peerId) === 'switch') {
          connectedSwitches.push(peerId);
        }
      }

      switchConnections.set(switchId, connectedSwitches);
    }

    // For a ring, each switch should connect to exactly 2 other switches
    const isValidRing = switches.every(switchId => {
      const connections = switchConnections.get(switchId) || [];
      return connections.length === 2;
    });

    if (!isValidRing) {
      return { isRing: false, ringOrder: [], confidence: 0 };
    }

    // Try to trace the ring order
    const ringOrder = this.traceRingOrder(switches[0], switchConnections);

    if (ringOrder.length === switches.length) {
      return {
        isRing: true,
        ringOrder,
        confidence: 0.9
      };
    }

    return { isRing: false, ringOrder: [], confidence: 0 };
  }

  // Trace the order of switches in the ring
  private traceRingOrder(startSwitch: string, switchConnections: Map<string, string[]>): string[] {
    const visited = new Set<string>();
    const ringOrder: string[] = [];
    let current = startSwitch;
    let previous: string | null = null;

    while (current && !visited.has(current)) {
      visited.add(current);
      ringOrder.push(current);

      const connections = switchConnections.get(current) || [];
      let next: string | null = null;

      // Find the next switch (not the previous one)
      for (const switchId of connections) {
        if (switchId !== previous) {
          next = switchId;
          break;
        }
      }

      previous = current;
      current = next || '';
    }

    return ringOrder;
  }

  // Assign ring-based roles to devices
  private assignRingRoles(ringDetection: { ringOrder: string[] }, deviceTypes: Map<string, 'host' | 'switch'>): void {
    const { ringOrder } = ringDetection;

    // Assign ring positions to switches
    ringOrder.forEach((switchId, index) => {
      this.nodeRoles.set(switchId, {
        role: 'ring_switch',
        layer: index, // Use layer as ring position
        confidence: 0.9
      });
    });

    // Assign ring positions to hosts (place them near their connected switches)
    for (const node of this.nodes) {
      if (node.type === 'device' && deviceTypes.get(node.id) === 'host') {
        // Find which switch this host connects to
        const connectivity = this.buildConnectivityGraph();
        const connections = connectivity.get(node.id) || new Map();

        let connectedSwitchIndex = 0;
        for (const peerId of Array.from(connections.keys())) {
          const switchIndex = ringOrder.indexOf(peerId);
          if (switchIndex !== -1) {
            connectedSwitchIndex = switchIndex;
            break;
          }
        }

        this.nodeRoles.set(node.id, {
          role: 'ring_host',
          layer: connectedSwitchIndex, // Same position as connected switch
          confidence: 0.9
        });
      }
    }
  }

  // Detect if the topology forms a dragonfly pattern
  private detectDragonflyTopology(connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): {
    isDragonfly: boolean;
    groups: Map<number, string[]>;
    confidence: number;
  } {
    const switches = Array.from(deviceTypes.entries())
      .filter(([_, type]) => type === 'switch')
      .map(([id]) => id);

    if (switches.length < 8) { // Need at least 2 groups of 4 switches
      return { isDragonfly: false, groups: new Map(), confidence: 0 };
    }

    // First check if this might be a spine-leaf topology instead
    // In spine-leaf: switches don't connect to other switches in the same layer
    const isSpineLeaf = this.detectSpineLeafPattern(switches, connectivity, deviceTypes);
    if (isSpineLeaf) {
      return { isDragonfly: false, groups: new Map(), confidence: 0 };
    }

    // Try to identify groups by analyzing switch-to-switch connectivity patterns
    const groups = this.identifyDragonflyGroups(switches, connectivity, deviceTypes);

    /*console.log('Dragonfly group detection results:', {
      groupCount: groups.size,
      groupSizes: Array.from(groups.values()).map(g => g.length),
      groups: Array.from(groups.entries()).map(([id, switches]) => ({
        id,
        switches: switches.map(s => this.nodes.find(n => n.id === s)?.name)
      }))
    });*/

    if (groups.size >= 2) {
      // Validate that each group forms a complete subgraph (all-to-all connections)
      let isValidDragonfly = true;
      let validationDetails = [];

      // Check for reasonable group sizes (dragonfly groups should be similar sizes)
      const groupSizes = Array.from(groups.values()).map(g => g.length);
      const minGroupSize = Math.min(...groupSizes);
      const maxGroupSize = Math.max(...groupSizes);
      const sizeVariance = maxGroupSize / minGroupSize;

      // Groups shouldn't vary too much in size (within 2x)
      if (sizeVariance > 2.0) {
        isValidDragonfly = false;
      }

      // Each group should have at least 4 switches (dragonfly groups are typically larger)
      // Small groups (2-3 nodes) can easily achieve high density through hierarchical patterns
      if (minGroupSize < 4) {
        isValidDragonfly = false;
      }

      for (const [groupId, groupSwitches] of Array.from(groups.entries())) {
        // Check if switches in this group are densely connected to each other
        const intraGroupConnections = this.countIntraGroupConnections(groupSwitches, connectivity);
        const expectedConnections = groupSwitches.length * (groupSwitches.length - 1); // All-to-all
        const actualConnections = intraGroupConnections * 2; // Bidirectional
        const density = actualConnections / expectedConnections;

        validationDetails.push({
          groupId,
          groupSize: groupSwitches.length,
          intraGroupConnections,
          expectedConnections,
          actualConnections,
          density
        });

        // Be strict about intra-group connectivity (require 80% density)
        if (density < 0.8) {
          isValidDragonfly = false;
          break;
        }

        // Additional check: detect if this is actually a hierarchical pattern, not all-to-all
        // In true dragonfly, all switches should have similar fan-out within the group
        const groupConnectivities = groupSwitches.map(switchId => {
          const connections = connectivity.get(switchId) || new Map();
          let intraGroupConnections = 0;
          for (const peerId of Array.from(connections.keys())) {
            if (groupSwitches.includes(peerId) && switchId !== peerId) {
              intraGroupConnections++;
            }
          }
          return intraGroupConnections;
        });

        const minConnectivity = Math.min(...groupConnectivities);
        const maxConnectivity = Math.max(...groupConnectivities);
        const connectivityVariance = maxConnectivity > 0 ? maxConnectivity / Math.max(1, minConnectivity) : 1;

        // In dragonfly all-to-all, connectivity should be uniform (all nodes connect to all others)
        // High variance suggests hierarchical pattern (some nodes are hubs, others are leaves)
        if (connectivityVariance > 1.5 && groupSwitches.length >= 3) {
          isValidDragonfly = false;
          break;
        }
      }

      //console.log('Dragonfly validation details:', validationDetails);

      if (isValidDragonfly) {
        // Additional check: verify inter-group connectivity is sparse
        let interGroupConnections = 0;
        let totalInterGroupPossible = 0;

        const groupArrays = Array.from(groups.values());
        for (let i = 0; i < groupArrays.length; i++) {
          for (let j = i + 1; j < groupArrays.length; j++) {
            const group1 = groupArrays[i];
            const group2 = groupArrays[j];

            // Count connections between these two groups
            for (const switch1 of group1) {
              const connections = connectivity.get(switch1) || new Map();
              for (const switch2 of group2) {
                if (connections.has(switch2)) {
                  interGroupConnections++;
                }
              }
            }

            totalInterGroupPossible += group1.length * group2.length;
          }
        }

        const interGroupDensity = interGroupConnections / totalInterGroupPossible;
        //console.log('Inter-group connectivity:', { interGroupConnections, totalInterGroupPossible, interGroupDensity });

        // Inter-group connectivity should be sparse (typical dragonfly has limited inter-group links)
        if (interGroupDensity > 0.3) {
          isValidDragonfly = false;
        }

        if (isValidDragonfly) {
          return {
            isDragonfly: true,
            groups,
            confidence: 0.9
          };
        }
      }
    }

    return { isDragonfly: false, groups: new Map(), confidence: 0 };
  }

  // Detect if the topology follows a spine-leaf pattern
  private detectSpineLeafPattern(switches: string[], connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): boolean {
    if (switches.length < 4) return false;

    // In a spine-leaf topology:
    // 1. Switches can be divided into exactly two groups (spine and leaf)
    // 2. Switches within the same group don't connect to each other
    // 3. Switches in different groups are highly interconnected
    // 4. Should be a classic 2-tier architecture, not 3+ tiers

    // Analyze switch naming patterns to identify potential spine/leaf roles
    const spinePatterns = ['spine', 'aggregation', 'agg', 'core'];
    const leafPatterns = ['leaf', 'tor', 'access', 'edge'];

    const potentialSpines: string[] = [];
    const potentialLeaves: string[] = [];
    const unclassified: string[] = [];

    for (const switchId of switches) {
      const node = this.nodes.find(n => n.id === switchId);
      if (!node) continue;

      const name = node.name.toLowerCase();
      const deviceType = (node.metadata?.deviceType || '').toLowerCase();

      if (spinePatterns.some(pattern => name.includes(pattern) || deviceType.includes(pattern))) {
        potentialSpines.push(switchId);
      } else if (leafPatterns.some(pattern => name.includes(pattern) || deviceType.includes(pattern))) {
        potentialLeaves.push(switchId);
      } else {
        unclassified.push(switchId);
      }
    }

    // If we can't classify by name, try connectivity analysis
    if (potentialSpines.length === 0 || potentialLeaves.length === 0) {
      return this.detectSpineLeafByConnectivity(switches, connectivity);
    }

    // Spine-leaf should only have 2 distinct switch types/roles
    // If we have unclassified switches or too many different types, it's likely a more complex topology
    if (unclassified.length > 0) {
      return false;
    }

    // Check for reasonable spine-to-leaf ratios (typically 1:2 to 1:8)
    const spineLeafRatio = potentialSpines.length / potentialLeaves.length;
    if (spineLeafRatio < 0.125 || spineLeafRatio > 2) {  // outside 1:8 to 2:1 range
      return false;
    }

    // Validate the spine-leaf pattern:
    // 1. Spines shouldn't connect to other spines
    // 2. Leaves shouldn't connect to other leaves
    // 3. Spines should connect to most/all leaves

    // Check intra-spine connections (should be minimal)
    const intraSpineConnections = this.countIntraGroupConnections(potentialSpines, connectivity);
    const maxSpineConnections = potentialSpines.length * (potentialSpines.length - 1) * 0.1; // Allow 10% tolerance

    // Check intra-leaf connections (should be minimal)
    const intraLeafConnections = this.countIntraGroupConnections(potentialLeaves, connectivity);
    const maxLeafConnections = potentialLeaves.length * (potentialLeaves.length - 1) * 0.1; // Allow 10% tolerance

    // Check spine-to-leaf connections (should be high)
    let spineToLeafConnections = 0;
    for (const spineId of potentialSpines) {
      const spineConnections = connectivity.get(spineId) || new Map();
      for (const leafId of potentialLeaves) {
        if (spineConnections.has(leafId)) {
          spineToLeafConnections++;
        }
      }
    }

    const expectedSpineToLeafConnections = potentialSpines.length * potentialLeaves.length;
    const actualSpineToLeafRatio = spineToLeafConnections / expectedSpineToLeafConnections;

    // For spine-leaf topology, we need good intra-layer isolation
    const hasGoodIntraLayerIsolation = (
      intraSpineConnections <= maxSpineConnections &&
      intraLeafConnections <= maxLeafConnections
    );

    // Be more lenient about inter-layer connectivity for spine-leaf detection
    // Spine-leaf should have reasonable interconnectivity, but dual-plane designs have ~50%
    const hasHighInterLayerConnectivity = actualSpineToLeafRatio >= 0.4;

    const isSpineLeaf = hasGoodIntraLayerIsolation && hasHighInterLayerConnectivity;

    /*console.log('Spine-leaf detection:', {
      potentialSpines: potentialSpines.length,
      potentialLeaves: potentialLeaves.length,
      unclassified: unclassified.length,
      spineLeafRatio,
      intraSpineConnections,
      maxSpineConnections,
      intraLeafConnections,
      maxLeafConnections,
      spineToLeafConnections,
      expectedSpineToLeafConnections,
      actualSpineToLeafRatio,
      hasGoodIntraLayerIsolation,
      hasHighInterLayerConnectivity,
      isSpineLeaf,
      spineNames: potentialSpines.map(id => this.nodes.find(n => n.id === id)?.name),
      leafNames: potentialLeaves.map(id => this.nodes.find(n => n.id === id)?.name),
      unclassifiedNames: unclassified.map(id => this.nodes.find(n => n.id === id)?.name)
    });*/

    return isSpineLeaf;
  }

  // Detect spine-leaf by analyzing connectivity patterns when naming is unclear
  private detectSpineLeafByConnectivity(switches: string[], connectivity: Map<string, Map<string, number>>): boolean {
    // Try to partition switches into two groups that minimize intra-group connections
    // and maximize inter-group connections

    if (switches.length < 4) return false;

    // Simple heuristic: switches with higher fan-out are likely spines
    const switchFanOut = new Map<string, number>();

    for (const switchId of switches) {
      const connections = connectivity.get(switchId) || new Map();
      let switchToSwitchConnections = 0;

      for (const peerId of Array.from(connections.keys())) {
        if (switches.includes(peerId)) {
          switchToSwitchConnections++;
        }
      }

      switchFanOut.set(switchId, switchToSwitchConnections);
    }

    // Sort by fan-out and try different partitions
    const sortedSwitches = switches.sort((a, b) =>
      (switchFanOut.get(b) || 0) - (switchFanOut.get(a) || 0)
    );

    // Try different split points
    for (let spineCount = 1; spineCount < switches.length; spineCount++) {
      const spines = sortedSwitches.slice(0, spineCount);
      const leaves = sortedSwitches.slice(spineCount);

      // Check if this partition has spine-leaf characteristics
      const intraSpineConnections = this.countIntraGroupConnections(spines, connectivity);
      const intraLeafConnections = this.countIntraGroupConnections(leaves, connectivity);

      let spineToLeafConnections = 0;
      for (const spineId of spines) {
        const spineConnections = connectivity.get(spineId) || new Map();
        for (const leafId of leaves) {
          if (spineConnections.has(leafId)) {
            spineToLeafConnections++;
          }
        }
      }

      const expectedSpineToLeafConnections = spines.length * leaves.length;
      const actualRatio = spineToLeafConnections / expectedSpineToLeafConnections;

      // Good spine-leaf partition: minimal intra-group, high inter-group connections
      if (intraSpineConnections === 0 && intraLeafConnections === 0 && actualRatio >= 0.7) {
        return true;
      }
    }

    return false;
  }

  // Identify dragonfly groups using connectivity clustering
  private identifyDragonflyGroups(switches: string[], connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): Map<number, string[]> {
    const groups = new Map<number, string[]>();
    const assigned = new Set<string>();
    let groupId = 0;

    for (const switchId of switches) {
      if (assigned.has(switchId)) continue;

      // Start a new group with this switch
      const group: string[] = [switchId];
      assigned.add(switchId);

      // Find switches that are densely connected to this switch
      const connections = connectivity.get(switchId) || new Map();
      const switchConnections: string[] = [];

      for (const peerId of Array.from(connections.keys())) {
        if (deviceTypes.get(peerId) === 'switch' && !assigned.has(peerId)) {
          switchConnections.push(peerId);
        }
      }

      // Add switches that are part of a dense cluster
      for (const candidateId of switchConnections) {
        if (assigned.has(candidateId)) continue;

        // Check if this candidate is well-connected to existing group members
        const candidateConnections = connectivity.get(candidateId) || new Map();
        let connectionsToGroup = 0;

        for (const groupMember of group) {
          if (candidateConnections.has(groupMember)) {
            connectionsToGroup++;
          }
        }

        // If connected to most group members, add to group
        if (connectionsToGroup >= Math.max(1, group.length * 0.6)) {
          group.push(candidateId);
          assigned.add(candidateId);
        }
      }

      // Only keep groups with at least 2 switches
      if (group.length >= 2) {
        groups.set(groupId++, group);
      }
    }

    return groups;
  }

  // Count intra-group connections
  private countIntraGroupConnections(groupSwitches: string[], connectivity: Map<string, Map<string, number>>): number {
    let connectionCount = 0;

    for (const switchId of groupSwitches) {
      const connections = connectivity.get(switchId) || new Map();
      for (const peerId of Array.from(connections.keys())) {
        if (groupSwitches.includes(peerId) && switchId !== peerId) {
          connectionCount++;
        }
      }
    }

    return connectionCount / 2; // Each connection counted twice
  }

  // Assign dragonfly-based roles to devices
  private assignDragonflyRoles(dragonflyDetection: { groups: Map<number, string[]> }, deviceTypes: Map<string, 'host' | 'switch'>): void {
    const { groups } = dragonflyDetection;

    // Assign dragonfly roles to switches
    for (const [groupId, groupSwitches] of Array.from(groups.entries())) {
      groupSwitches.forEach((switchId, index) => {
        this.nodeRoles.set(switchId, {
          role: 'dragonfly_switch',
          layer: index, // Position within group
          group: groupId,
          confidence: 0.9
        });
      });
    }

    // Assign dragonfly roles to hosts
    for (const node of this.nodes) {
      if (node.type === 'device' && deviceTypes.get(node.id) === 'host') {
        // Find which switch group this host connects to
        const connectivity = this.buildConnectivityGraph();
        const connections = connectivity.get(node.id) || new Map();

        let connectedGroup = 0;
        let connectedSwitchIndex = 0;

        for (const peerId of Array.from(connections.keys())) {
          // Find which group this peer belongs to
          for (const [groupId, groupSwitches] of Array.from(groups.entries())) {
            const switchIndex = groupSwitches.indexOf(peerId);
            if (switchIndex !== -1) {
              connectedGroup = groupId;
              connectedSwitchIndex = switchIndex;
              break;
            }
          }
        }

        this.nodeRoles.set(node.id, {
          role: 'dragonfly_host',
          layer: connectedSwitchIndex, // Same position as connected switch within group
          group: connectedGroup,
          confidence: 0.9
        });
      }
    }
  }

  // Assign hierarchical roles (extracted from original logic)
  private assignHierarchicalRoles(connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): void {
    // Analyze switch connectivity patterns to determine role diversity
    const switches = Array.from(deviceTypes.entries())
      .filter(([_, type]) => type === 'switch')
      .map(([id]) => id);

    // Check if all switches have the same connectivity pattern (single role)
    const isSingleSwitchRole = this.hasSingleSwitchRole(switches, connectivity, deviceTypes);

    if (isSingleSwitchRole) {
      // Single role topology - use simple layering
      for (const node of this.nodes) {
        if (node.type === 'device') {
          const role = this.determineDeviceRoleByConnectivity(node, connectivity, deviceTypes, isSingleSwitchRole);
          this.nodeRoles.set(node.id, role);
        } else {
          this.nodeRoles.set(node.id, {
            role: 'unknown',
            layer: 1,
            confidence: 1.0
          });
        }
      }
    } else {
      // Multi-role topology - use hierarchical tier analysis
      const switchTiers = this.analyzeSwitchHierarchy(switches, connectivity, deviceTypes);
      this.assignTierBasedRoles(switchTiers, deviceTypes);
    }
  }

  // Determine if all switches have the same connectivity pattern (single role)
  private hasSingleSwitchRole(
    switches: string[],
    connectivity: Map<string, Map<string, number>>,
    deviceTypes: Map<string, 'host' | 'switch'>
  ): boolean {
    if (switches.length <= 1) return true; // Single or no switches = single role

    // Analyze connectivity patterns for all switches
    const switchPatterns = switches.map(switchId => {
      const connections = connectivity.get(switchId) || new Map();

      let hostsConnected = 0;
      let switchesConnected = 0;

      for (const peerId of Array.from(connections.keys())) {
        const peerType = deviceTypes.get(peerId);
        if (peerType === 'host') {
          hostsConnected++;
        } else if (peerType === 'switch') {
          switchesConnected++;
        }
      }

      return {
        switchId,
        hasHosts: hostsConnected > 0,
        hasSwitches: switchesConnected > 0,
        hostCount: hostsConnected,
        switchCount: switchesConnected
      };
    });

    // Check if all switches have the same connectivity pattern
    const firstPattern = switchPatterns[0];
    const allSamePattern = switchPatterns.every(pattern =>
      pattern.hasHosts === firstPattern.hasHosts &&
      pattern.hasSwitches === firstPattern.hasSwitches
    );

    return allSamePattern;
  }

  // Analyze switch hierarchy to determine tier-based layering
  private analyzeSwitchHierarchy(
    switches: string[],
    connectivity: Map<string, Map<string, number>>,
    deviceTypes: Map<string, 'host' | 'switch'>
  ): Map<number, string[]> {
    // Build switch-to-switch connectivity graph
    const switchGraph = new Map<string, Set<string>>();
    switches.forEach(switchId => switchGraph.set(switchId, new Set()));

    switches.forEach(switchId => {
      const connections = connectivity.get(switchId) || new Map();
      connections.forEach((_, peerId) => {
        if (deviceTypes.get(peerId) === 'switch') {
          switchGraph.get(switchId)?.add(peerId);
        }
      });
    });

    // Find switches that connect to hosts (leaf layer)
    const leafSwitches = switches.filter(switchId => {
      const connections = connectivity.get(switchId) || new Map();
      return Array.from(connections.keys()).some(peerId => deviceTypes.get(peerId) === 'host');
    });

    // Build tiers starting from leaf layer
    const tiers = new Map<number, string[]>();
    const assigned = new Set<string>();

    // Tier 0: Leaf switches (connect to hosts)
    if (leafSwitches.length > 0) {
      tiers.set(0, leafSwitches);
      leafSwitches.forEach(s => assigned.add(s));
    }

    let currentTier = 1;
    let remainingSwitches = switches.filter(s => !assigned.has(s));

    // Build higher tiers by analyzing connectivity distance from leaves
    while (remainingSwitches.length > 0 && currentTier < 10) { // Safety limit
      const prevTierIndex = currentTier - 1;
      const previousTierSwitches = tiers.get(prevTierIndex) || [];

      // eslint-disable-next-line no-loop-func
      const nextTierSwitches = remainingSwitches.filter(switchId => {
        // This switch belongs to current tier if it connects to previous tier switches
        const connections = switchGraph.get(switchId) || new Set();
        return previousTierSwitches.some(prevSwitchId => connections.has(prevSwitchId));
      });

      if (nextTierSwitches.length === 0) {
        // No switches connect to previous tier - put remaining switches in current tier
        tiers.set(currentTier, remainingSwitches);
        remainingSwitches.forEach(s => assigned.add(s));
        break;
      }

      tiers.set(currentTier, nextTierSwitches);
      nextTierSwitches.forEach(s => assigned.add(s));
      remainingSwitches = remainingSwitches.filter(s => !assigned.has(s));
      currentTier++;
    }

    /*console.log('Switch hierarchy analysis:', {
      totalSwitches: switches.length,
      leafSwitches: leafSwitches.length,
      tiers: Array.from(tiers.entries()).map(([tier, switches]) => ({
        tier,
        switches: switches.map(s => this.nodes.find(n => n.id === s)?.name),
        count: switches.length
      }))
    });*/

    return tiers;
  }

  // Assign roles based on hierarchical tier analysis
  private assignTierBasedRoles(switchTiers: Map<number, string[]>, deviceTypes: Map<string, 'host' | 'switch'>): void {
    const maxTier = Math.max(...Array.from(switchTiers.keys()));
    const connectivity = this.buildConnectivityGraph();

    // Separate scale-up switches from regular leaf switches
    const leafTierSwitches = switchTiers.get(0) || []; // Tier 0 = connects to hosts
    const scaleUpSwitches: string[] = [];
    const regularLeafSwitches: string[] = [];

    leafTierSwitches.forEach(switchId => {
      if (this.isScaleUpSwitch(switchId, connectivity, deviceTypes)) {
        scaleUpSwitches.push(switchId);
      } else {
        regularLeafSwitches.push(switchId);
      }
    });

    // Assign switch roles based on tiers (reverse order so top tier gets layer 0)
    switchTiers.forEach((switchIds, tier) => {
      if (tier === 0) {
        // Handle leaf tier specially - separate regular leaf from scale-up
        regularLeafSwitches.forEach(switchId => {
          this.nodeRoles.set(switchId, {
            role: 'leaf',
            layer: maxTier - tier, // Above hosts
            confidence: 0.9
          });
        });
        // Scale-up switches will be handled separately below
      } else {
        // Higher tiers - normal spine switches
        const layer = maxTier - tier; // Invert: highest tier → layer 0
        const role = layer === 0 ? 'spine' : 'leaf';

        switchIds.forEach(switchId => {
          this.nodeRoles.set(switchId, {
            role,
            layer,
            confidence: 0.9
          });
        });
      }
    });

    // Assign host roles (middle layer)
    const hostLayer = maxTier + 1;
    for (const node of this.nodes) {
      if (node.type === 'device' && !this.nodeRoles.has(node.id)) {
        const nodeType = deviceTypes.get(node.id);
        if (nodeType === 'host') {
          this.nodeRoles.set(node.id, {
            role: 'server',
            layer: hostLayer,
            confidence: 0.95
          });
        } else {
          this.nodeRoles.set(node.id, {
            role: 'unknown',
            layer: hostLayer,
            confidence: 0.5
          });
        }
      } else if (node.type !== 'device') {
        // Non-device nodes
        this.nodeRoles.set(node.id, {
          role: 'unknown',
          layer: 1,
          confidence: 1.0
        });
      }
    }

    // Assign scale-up switches (below hosts)
    scaleUpSwitches.forEach(switchId => {
      this.nodeRoles.set(switchId, {
        role: 'leaf',
        layer: hostLayer + 1, // Below hosts
        confidence: 0.9
      });
    });

    /*console.log('Tier-based role assignment:', {
      maxTier,
      hostLayer,
      regularLeafSwitches: regularLeafSwitches.length,
      scaleUpSwitches: scaleUpSwitches.length,
      assignments: Array.from(this.nodeRoles.entries()).map(([id, role]) => ({
        node: this.nodes.find(n => n.id === id)?.name,
        role: role.role,
        layer: role.layer
      }))
    });*/
  }

  // Detect if a switch is a scale-up switch (provides intra-host interconnect)
  private isScaleUpSwitch(switchId: string, connectivity: Map<string, Map<string, number>>, deviceTypes: Map<string, 'host' | 'switch'>): boolean {
    // Scale-up switches typically:
    // 1. Connect only to hosts (no uplinks to other switches)
    // 2. Have naming patterns suggesting scale-up functionality
    // 3. Are used for GPU-to-GPU or intra-host communication

    const connections = connectivity.get(switchId) || new Map();
    const connectedSwitches = Array.from(connections.keys()).filter(peerId => deviceTypes.get(peerId) === 'switch');
    const hasUplinks = connectedSwitches.length > 0;

    if (hasUplinks) {
      return false; // Has uplinks to other switches, not scale-up
    }

    // Check naming patterns for scale-up indicators
    const node = this.nodes.find(n => n.id === switchId);
    const deviceType = node?.metadata?.deviceType || '';
    const nodeName = node?.name?.toLowerCase() || '';

    const scaleUpIndicators = [
      'scale_up', 'scaleup', 'scale-up',
      'gpu_switch', 'gpuswitch', 'gpu-switch',
      'interconnect', 'fabric', 'nvswitch'
    ];

    const hasScaleUpNaming = scaleUpIndicators.some(indicator =>
      deviceType.toLowerCase().includes(indicator) ||
      nodeName.includes(indicator)
    );

    // For now, without clear naming patterns, assume switches that only connect to hosts
    // in a multi-tier topology might be regular leaf switches (like TIER_0)
    // Scale-up detection would need more context or explicit naming
    return hasScaleUpNaming;
  }

  // Force ring roles - arrange all switches in a ring regardless of topology
  private forceRingRoles(deviceTypes: Map<string, 'host' | 'switch'>): void {
    const switches = Array.from(deviceTypes.entries())
      .filter(([_, type]) => type === 'switch')
      .map(([id]) => id);

    // Assign ring positions to all switches
    switches.forEach((switchId, index) => {
      this.nodeRoles.set(switchId, {
        role: 'ring_switch',
        layer: index, // Use index as ring position
        confidence: 1.0 // High confidence since it's forced
      });
    });

    // Assign ring positions to hosts
    for (const node of this.nodes) {
      if (node.type === 'device' && deviceTypes.get(node.id) === 'host') {
        // Find which switch this host connects to (or assign to first switch if unclear)
        const connectivity = this.buildConnectivityGraph();
        const connections = connectivity.get(node.id) || new Map();

        let connectedSwitchIndex = 0;
        for (const peerId of Array.from(connections.keys())) {
          const switchIndex = switches.indexOf(peerId);
          if (switchIndex !== -1) {
            connectedSwitchIndex = switchIndex;
            break;
          }
        }

        this.nodeRoles.set(node.id, {
          role: 'ring_host',
          layer: connectedSwitchIndex,
          confidence: 1.0
        });
      }
    }

    // Assign neutral roles to non-device nodes
    for (const node of this.nodes) {
      if (node.type !== 'device') {
        this.nodeRoles.set(node.id, {
          role: 'unknown',
          layer: 1,
          confidence: 1.0
        });
      }
    }
  }

  // Force dragonfly roles - create artificial groups from switches
  private forceDragonflyRoles(deviceTypes: Map<string, 'host' | 'switch'>): void {
    const switches = Array.from(deviceTypes.entries())
      .filter(([_, type]) => type === 'switch')
      .map(([id]) => id);

    // Create artificial groups by dividing switches
    const groupSize = Math.max(2, Math.floor(switches.length / 4)); // Aim for ~4 groups
    const groups = new Map<number, string[]>();

    // Distribute switches into groups
    switches.forEach((switchId, index) => {
      const groupId = Math.floor(index / groupSize);
      if (!groups.has(groupId)) {
        groups.set(groupId, []);
      }
      groups.get(groupId)!.push(switchId);
    });

    // Assign dragonfly roles to switches
    for (const [groupId, groupSwitches] of Array.from(groups.entries())) {
      groupSwitches.forEach((switchId, index) => {
        this.nodeRoles.set(switchId, {
          role: 'dragonfly_switch',
          layer: index, // Position within group
          group: groupId,
          confidence: 1.0 // High confidence since it's forced
        });
      });
    }

    // Assign dragonfly roles to hosts
    for (const node of this.nodes) {
      if (node.type === 'device' && deviceTypes.get(node.id) === 'host') {
        // Find which switch this host connects to (or assign to first group if unclear)
        const connectivity = this.buildConnectivityGraph();
        const connections = connectivity.get(node.id) || new Map();

        let connectedGroup = 0;
        let connectedSwitchIndex = 0;

        // Try to find connected switch and its group
        for (const peerId of Array.from(connections.keys())) {
          for (const [groupId, groupSwitches] of Array.from(groups.entries())) {
            const switchIndex = groupSwitches.indexOf(peerId);
            if (switchIndex !== -1) {
              connectedGroup = groupId;
              connectedSwitchIndex = switchIndex;
              break;
            }
          }
          if (connectedGroup !== 0 || connectedSwitchIndex !== 0) break;
        }

        this.nodeRoles.set(node.id, {
          role: 'dragonfly_host',
          layer: connectedSwitchIndex,
          group: connectedGroup,
          confidence: 1.0
        });
      }
    }

    // Assign neutral roles to non-device nodes
    for (const node of this.nodes) {
      if (node.type !== 'device') {
        this.nodeRoles.set(node.id, {
          role: 'unknown',
          layer: 1,
          confidence: 1.0
        });
      }
    }
  }

  // Determine device role based on connectivity patterns - topology-aware approach
  private determineDeviceRoleByConnectivity(
    node: VisualizationNode,
    connectivity: Map<string, Map<string, number>>,
    deviceTypes: Map<string, 'host' | 'switch'>,
    isSingleSwitchRole: boolean = false
  ): NodeRole {
    const connections = connectivity.get(node.id) || new Map();
    const nodeType = deviceTypes.get(node.id);

    // Hosts are always at the bottom layer
    if (nodeType === 'host') {
      return {
        role: 'server',
        layer: 2, // Bottom layer for compute nodes
        confidence: 0.95
      };
    }

    // For switches, analyze what they connect to
    if (nodeType === 'switch') {
      const connectedHosts = new Set<string>();
      const connectedSwitches = new Set<string>();

      // Categorize connected devices
      for (const peerId of Array.from(connections.keys())) {
        const peerType = deviceTypes.get(peerId);
        if (peerType === 'host') {
          connectedHosts.add(peerId);
        } else if (peerType === 'switch') {
          connectedSwitches.add(peerId);
        }
      }

      const hasHostConnections = connectedHosts.size > 0;
      const hasSwitchConnections = connectedSwitches.size > 0;

      // Determine switch layer based on connectivity and role diversity
      if (!hasSwitchConnections && hasHostConnections) {
        // Only connects to hosts
        if (isSingleSwitchRole) {
          // Single switch role - all switches go above hosts (TOR/access layer)
          return {
            role: 'leaf',
            layer: 1, // Above hosts - top-of-rack/access switches
            confidence: 0.9
          };
        } else {
          // Multiple switch roles - this is a scale-up switch (below hosts)
          return {
            role: 'leaf',
            layer: 3, // Below hosts - scale-up switch for host interconnect
            confidence: 0.9
          };
        }
      } else if (hasHostConnections && hasSwitchConnections) {
        // Connects to both hosts and switches - aggregation/leaf switch (above hosts)
        return {
          role: 'leaf',
          layer: 1, // Above hosts - aggregation tier
          confidence: 0.9
        };
      } else if (!hasHostConnections && hasSwitchConnections) {
        // Only connects to switches - spine/core switch (top layer)
        return {
          role: 'spine',
          layer: 0, // Top layer - interconnects other switches
          confidence: 0.9
        };
      } else {
        // No connections or unclear pattern
        return {
          role: 'unknown',
          layer: 2,
          confidence: 0.3
        };
      }
    }

    // Fallback for unrecognized device types
    return {
      role: 'unknown',
      layer: 2,
      confidence: 0.3
    };
  }

  // Legacy method - keeping for backward compatibility but not used
  private determineDeviceRole(node: VisualizationNode, connectivity: Map<string, Map<string, number>>): NodeRole {
    const connections = connectivity.get(node.id) || new Map();
    const connectionCount = Array.from(connections.values()).reduce((sum, count) => sum + count, 0);
    const uniquePeers = connections.size;

    // Get device type hints from metadata or name
    const deviceType = node.metadata?.deviceType || '';
    const nodeName = node.name.toLowerCase();

    // Pattern-based role detection
    let role: NodeRole['role'] = 'unknown';
    let layer = 1;
    let confidence = 0.5;

    // 1. Check explicit device type or name patterns
    if (deviceType.includes('SPINE') || deviceType.includes('spine') || nodeName.includes('spine')) {
      role = 'spine';
      layer = 0;
      confidence = 0.9;
    } else if (deviceType === 'SPINE_SWITCH' || deviceType === 'spine-switch' || nodeName.includes('spine')) {
      role = 'spine';
      layer = 0;
      confidence = 0.95;
    } else if (deviceType === 'SCALE_OUT_SWITCH') {
      // Scale-out switches typically connect to many devices (spine-like)
      role = 'spine';
      layer = 0;
      confidence = 0.9;
    } else if (deviceType.includes('LEAF') || deviceType.includes('leaf') || nodeName.includes('leaf')) {
      role = 'leaf';
      layer = 1;
      confidence = 0.9;
    } else if (deviceType === 'LEAF_SWITCH') {
      role = 'leaf';
      layer = 1;
      confidence = 0.95;
    } else if (deviceType === 'SCALE_UP_SWITCH') {
      // Scale-up switches typically aggregate connections (leaf-like)
      role = 'leaf';
      layer = 1;
      confidence = 0.9;
    } else if (deviceType.includes('switch') || nodeName.includes('switch')) {
      // Generic switches default to leaf
      role = 'leaf';
      layer = 1;
      confidence = 0.7;
    } else if (deviceType === 'HOST' || deviceType === 'GPU_SERVER' || deviceType.includes('server') || nodeName.includes('server') || nodeName.includes('gpu') || nodeName.includes('host')) {
      role = 'server';
      layer = 2;
      confidence = 0.8;
    } else if (deviceType.includes('storage') || nodeName.includes('storage')) {
      role = 'storage';
      layer = 2;
      confidence = 0.8;
    } else {
      // 2. Analyze connectivity patterns for spine-leaf topology
      const connectedDeviceTypes = new Set<string>();
      for (const peerId of Array.from(connections.keys())) {
        const peerNode = this.nodes.find(n => n.id === peerId);
        if (peerNode?.metadata?.deviceType) {
          connectedDeviceTypes.add(peerNode.metadata.deviceType);
        }

        // Check peer names for hints
        const peerName = peerNode?.name.toLowerCase() || '';
        if (peerName.includes('spine')) connectedDeviceTypes.add('spine');
        if (peerName.includes('switch') || peerName.includes('leaf')) connectedDeviceTypes.add('switch');
        if (peerName.includes('server')) connectedDeviceTypes.add('server');
      }

      // High fan-out to many different devices = likely spine
      if (uniquePeers >= 4 && connectionCount >= 8) {
        role = 'spine';
        layer = 0;
        confidence = 0.7;
      }
      // Medium fan-out, connects both up and down = likely leaf
      else if (uniquePeers >= 2 && connectionCount >= 4) {
        // Check if connects to both spine-like and server-like devices
        const hasUplinks = Array.from(connectedDeviceTypes).some(type =>
          type.includes('spine') || type === 'spine-switch'
        );
        const hasDownlinks = Array.from(connectedDeviceTypes).some(type =>
          type.includes('server') || type === 'gpu-server'
        );

        if (hasUplinks && hasDownlinks) {
          role = 'leaf';
          layer = 1;
          confidence = 0.8;
        } else if (hasUplinks) {
          role = 'server';
          layer = 2;
          confidence = 0.6;
        } else {
          role = 'leaf';
          layer = 1;
          confidence = 0.5;
        }
      }
      // Low connectivity = likely end device (server)
      else if (connectionCount <= 16) {
        role = 'server';
        layer = 2;
        confidence = 0.6;
      }
    }

    return { role, layer, confidence };
  }

  // Apply role-based positioning with vertical layer separation
  applyRoleBasedLayout(width: number, height: number): VisualizationNode[] {
    if (!this.config.enableVerticalSeparation) {
      return this.nodes;
    }

    // Separate device nodes from container nodes (racks, etc.)
    const deviceNodes = this.nodes.filter(node => node.type === 'device');
    const containerNodes = this.nodes.filter(node => node.type !== 'device');

    // Only apply role-based layout to device nodes
    if (deviceNodes.length === 0) {
      return this.nodes; // No devices to layout
    }

    // Create a temporary layout engine for just device nodes
    const deviceLayoutEngine = new AutoLayoutEngine(deviceNodes, this.links, this.config);
    const roleAnalysis = deviceLayoutEngine.analyzeNodeRoles();
    const layeredNodes = deviceLayoutEngine.groupNodesByLayer(roleAnalysis);
    const positionedDevices = deviceLayoutEngine.positionNodesByLayer(layeredNodes, width, height);

    // Position container nodes (like racks) separately
    const positionedContainers = this.positionContainerNodes(containerNodes, width, height, positionedDevices);

    // Combine positioned devices with positioned containers
    const result: VisualizationNode[] = [];

    for (const node of this.nodes) {
      if (node.type === 'device') {
        // Find the positioned version of this device
        const positionedDevice = positionedDevices.find(p => p.id === node.id);
        result.push(positionedDevice || node);
      } else {
        // Find the positioned version of this container
        const positionedContainer = positionedContainers.find(p => p.id === node.id);
        result.push(positionedContainer || node);
      }
    }

    return result;
  }

  // Position container nodes (racks, networks, etc.) separately from devices
  positionContainerNodes(containerNodes: VisualizationNode[], width: number, height: number, positionedDevices: VisualizationNode[]): VisualizationNode[] {
    if (containerNodes.length === 0) return [];

    const positionedNodes: VisualizationNode[] = [];

    // Separate different types of containers
    const racks = containerNodes.filter(node => node.type === 'rack');
    const otherContainers = containerNodes.filter(node => node.type !== 'rack');

    // Position racks in a centered horizontal line below the main topology
    if (racks.length > 0) {
      // Find the bottom-most Y position of all devices
      const deviceYPositions = positionedDevices.map(device => device.y || 0);
      const maxDeviceY = positionedDevices.length > 0
        ? Math.max(...deviceYPositions)
        : height * 0.5;


      // Position racks with some margin below the lowest device
      const rackMargin = 100; // 100px margin below devices
      const rackY = maxDeviceY + rackMargin;
      const rackSpacing = 120; // Fixed spacing of 120px between racks
      const totalWidth = (racks.length - 1) * rackSpacing; // Total width needed
      const startX = (width - totalWidth) / 2; // Center the rack line

      racks.forEach((node, index) => {
        const x = racks.length === 1 ? width / 2 : startX + (index * rackSpacing);

        // Minimal jitter for racks
        const jitter = 5;
        const jitteredX = x + (Math.random() - 0.5) * jitter;
        const jitteredY = rackY + (Math.random() - 0.5) * jitter * 0.3;

        positionedNodes.push({
          ...node,
          x: jitteredX,
          y: jitteredY
        });
      });
    }

    // Position other container types (networks, pods, etc.) - keep them in default positions for now
    otherContainers.forEach(node => {
      positionedNodes.push({
        ...node,
        x: node.x || width / 2,
        y: node.y || height / 2
      });
    });

    return positionedNodes;
  }

  // Group nodes by their assigned layer
  groupNodesByLayer(roleAnalysis: Map<string, NodeRole>): Map<number, VisualizationNode[]> {
    const layers = new Map<number, VisualizationNode[]>();

    for (const node of this.nodes) {
      const role = roleAnalysis.get(node.id);
      const layer = role?.layer ?? 1; // Default to middle layer

      if (!layers.has(layer)) {
        layers.set(layer, []);
      }
      layers.get(layer)!.push(node);
    }

    return layers;
  }

  // Position nodes within their assigned layers
  positionNodesByLayer(layeredNodes: Map<number, VisualizationNode[]>, width: number, height: number): VisualizationNode[] {

    // Check topology type for specialized positioning
    const hasDragonflyNodes = Array.from(layeredNodes.values()).some(nodes =>
      nodes.some(node => {
        const role = this.nodeRoles.get(node.id);
        return role && (role.role === 'dragonfly_switch' || role.role === 'dragonfly_host');
      })
    );

    const hasRingNodes = Array.from(layeredNodes.values()).some(nodes =>
      nodes.some(node => {
        const role = this.nodeRoles.get(node.id);
        return role && (role.role === 'ring_switch' || role.role === 'ring_host');
      })
    );

    if (hasDragonflyNodes) {
      // Dragonfly topology: arrange groups in circles
      return this.positionDragonflyNodes(layeredNodes, width, height);
    } else if (hasRingNodes) {
      // Ring topology: arrange nodes in a circle
      return this.positionRingNodes(layeredNodes, width, height);
    } else {
      // Hierarchical topology: arrange nodes in horizontal layers
      return this.positionHierarchicalNodes(layeredNodes, width, height);
    }
  }

  // Position nodes in a ring topology using circular layout
  private positionRingNodes(layeredNodes: Map<number, VisualizationNode[]>, width: number, height: number): VisualizationNode[] {
    const positionedNodes: VisualizationNode[] = [];
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.35; // Use 35% of the smaller dimension

    // Separate ring switches and hosts
    const ringSwitches: VisualizationNode[] = [];
    const ringHosts: VisualizationNode[] = [];

    for (const nodes of Array.from(layeredNodes.values())) {
      for (const node of nodes) {
        const role = this.nodeRoles.get(node.id);
        if (role?.role === 'ring_switch') {
          ringSwitches.push(node);
        } else if (role?.role === 'ring_host') {
          ringHosts.push(node);
        }
      }
    }

    // Position ring switches in a circle
    ringSwitches.forEach((node, index) => {
      const role = this.nodeRoles.get(node.id);
      const ringPosition = role?.layer || index; // Use layer as ring position
      const angle = (ringPosition / ringSwitches.length) * 2 * Math.PI;

      const x = centerX + radius * Math.cos(angle);
      const y = centerY + radius * Math.sin(angle);

      positionedNodes.push({
        ...node,
        x,
        y
      });
    });

    // Position hosts near their connected switches
    ringHosts.forEach((node) => {
      const role = this.nodeRoles.get(node.id);
      const switchPosition = role?.layer || 0; // Same position as connected switch
      const angle = (switchPosition / ringSwitches.length) * 2 * Math.PI;

      // Place hosts outside the ring, further from center
      const hostRadius = radius * 1.4;
      const x = centerX + hostRadius * Math.cos(angle);
      const y = centerY + hostRadius * Math.sin(angle);

      positionedNodes.push({
        ...node,
        x,
        y
      });
    });

    /*console.log('Applied ring layout:', {
      ringSwitches: ringSwitches.length,
      ringHosts: ringHosts.length,
      radius,
      center: { x: centerX, y: centerY }
    });*/

    return positionedNodes;
  }

  // Position nodes in a dragonfly topology using multiple circular layouts
  private positionDragonflyNodes(layeredNodes: Map<number, VisualizationNode[]>, width: number, height: number): VisualizationNode[] {
    const positionedNodes: VisualizationNode[] = [];

    // Group nodes by their dragonfly group
    const groups = new Map<number, { switches: VisualizationNode[], hosts: VisualizationNode[] }>();

    for (const nodes of Array.from(layeredNodes.values())) {
      for (const node of nodes) {
        const role = this.nodeRoles.get(node.id);
        if (role && (role.role === 'dragonfly_switch' || role.role === 'dragonfly_host')) {
          const groupId = role.group || 0;

          if (!groups.has(groupId)) {
            groups.set(groupId, { switches: [], hosts: [] });
          }

          const group = groups.get(groupId)!;
          if (role.role === 'dragonfly_switch') {
            group.switches.push(node);
          } else {
            group.hosts.push(node);
          }
        }
      }
    }

    // Calculate group positions in a larger circle
    const numGroups = groups.size;
    const centerX = width / 2;
    const centerY = height / 2;
    const groupRadius = Math.min(width, height) * 0.2; // Smaller ring - distance between group centers

    /*console.log('Positioning dragonfly topology:', {
      numGroups,
      groupRadius,
      center: { x: centerX, y: centerY }
    });*/

    let groupIndex = 0;
    for (const [, { switches, hosts }] of Array.from(groups.entries())) {
      // Position this group's center
      const groupAngle = (groupIndex / numGroups) * 2 * Math.PI;
      const groupCenterX = centerX + groupRadius * Math.cos(groupAngle);
      const groupCenterY = centerY + groupRadius * Math.sin(groupAngle);

      // Position switches within this group on a line touching the outer ring
      const lineLength = Math.min(width, height) * 0.12; // Length of the line for switches
      const lineOffset = groupRadius; // Distance from center to the line (at the ring)

      // Calculate line start and end points (perpendicular to the radial line)
      const perpAngle = groupAngle + Math.PI / 2; // Perpendicular to group angle
      const lineStartX = groupCenterX + lineOffset * Math.cos(groupAngle) - (lineLength / 2) * Math.cos(perpAngle);
      const lineStartY = groupCenterY + lineOffset * Math.sin(groupAngle) - (lineLength / 2) * Math.sin(perpAngle);

      switches.forEach((node, index) => {
        // Position switches evenly along the line
        const t = switches.length > 1 ? index / (switches.length - 1) : 0.5; // 0 to 1
        const x = lineStartX + lineLength * t * Math.cos(perpAngle);
        const y = lineStartY + lineLength * t * Math.sin(perpAngle);

        positionedNodes.push({
          ...node,
          x,
          y
        });
      });

      // Position hosts outside their group line, extending radially outward
      hosts.forEach((node) => {
        const role = this.nodeRoles.get(node.id);
        const switchPosition = role?.layer || 0;

        // Find the corresponding switch position on the line
        const t = switches.length > 1 ? switchPosition / (switches.length - 1) : 0.5;
        const switchX = lineStartX + lineLength * t * Math.cos(perpAngle);
        const switchY = lineStartY + lineLength * t * Math.sin(perpAngle);

        // Position host further out from the center, aligned with its switch
        const hostDistance = Math.min(width, height) * 0.36; // Fixed distance, outside the ring
        const hostAngle = Math.atan2(switchY - centerY, switchX - centerX);
        const x = centerX + hostDistance * Math.cos(hostAngle);
        const y = centerY + hostDistance * Math.sin(hostAngle);

        positionedNodes.push({
          ...node,
          x,
          y
        });
      });

      groupIndex++;
    }

    /*console.log('Applied dragonfly layout:', {
      groups: Array.from(groups.entries()).map(([groupId, { switches, hosts }]) => ({
        groupId,
        switches: switches.length,
        hosts: hosts.length
      }))
    });*/

    return positionedNodes;
  }

  // Position nodes in hierarchical layers (original linear layout)
  private positionHierarchicalNodes(layeredNodes: Map<number, VisualizationNode[]>, width: number, height: number): VisualizationNode[] {
    const positionedNodes: VisualizationNode[] = [];
    const numLayers = layeredNodes.size;

    // Calculate vertical positions for each layer with clear separation
    const layerHeight = height / (numLayers + 1);

    for (const [layer, nodes] of Array.from(layeredNodes.entries())) {
      // All nodes in the same layer get the SAME Y coordinate (horizontal level)
      const y = layerHeight * (layer + 1);

      // Spread nodes horizontally across the width
      const nodeSpacing = width / (nodes.length + 1);

      nodes.forEach((node, index) => {
        const x = nodeSpacing * (index + 1);

        // Add minimal randomization to prevent exact overlap while keeping horizontal alignment
        const jitter = 10;
        const jitteredX = x + (Math.random() - 0.5) * jitter;
        // Keep Y very close to the layer position to maintain horizontal alignment
        const jitteredY = y + (Math.random() - 0.5) * jitter * 0.3;

        positionedNodes.push({
          ...node,
          x: jitteredX,
          y: jitteredY
        });
      });
    }

    /*console.log('Applied role-based layout:', {
      layers: Array.from(layeredNodes.entries()).map(([layer, nodes]) => ({
        layer,
        count: nodes.length,
        roles: Array.from(new Set(nodes.map(n => this.nodeRoles.get(n.id)?.role))),
        nodeNames: nodes.map(n => n.name)
      }))
    });*/

    return positionedNodes;
  }

  // Get role information for a specific node
  getNodeRole(nodeId: string): NodeRole | undefined {
    return this.nodeRoles.get(nodeId);
  }

  // Get all nodes of a specific role
  getNodesByRole(role: NodeRole['role']): VisualizationNode[] {
    return this.nodes.filter(node => {
      const nodeRole = this.nodeRoles.get(node.id);
      return nodeRole?.role === role;
    });
  }
}